import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '#lib/cn.ts';

/**
 * The `Title` override from `apps/web`'s Mantine theme: each heading level picks its own
 * font family, size, weight and case, which is why this is a component rather than a
 * class per call site. Levels 1-2 are the display face, 3-5 the mono face (4-5 uppercase), 6 the
 * body face — the same split Mantine's `order` prop drove, so `<Title order={n}>` becomes
 * `<Heading order={n}>` and the element stays `h{n}`.
 */
const headingVariants = cva('', {
  variants: {
    order: {
      1: 'font-display text-[29px]/[1.1] font-light',
      2: 'font-display text-[26px]/[1.2] font-light',
      3: 'font-mono text-[23px]/[1.25] font-semibold',
      4: 'font-mono text-xl/[1.3] font-medium uppercase',
      5: 'font-mono text-lg/[1.4] font-semibold uppercase',
      6: 'font-sans text-base/[1] font-semibold',
    },
  },
  defaultVariants: {
    order: 1,
  },
});

type HeadingOrder = NonNullable<VariantProps<typeof headingVariants>['order']>;

function Heading({
  className,
  order = 1,
  ...props
}: React.ComponentProps<'h1'> & {
  order?: HeadingOrder;
}) {
  const Tag = `h${order}` as const;

  return <Tag data-slot='heading' className={cn(headingVariants({ order }), className)} {...props} />;
}

export type { HeadingOrder };
export { Heading, headingVariants };
